/**
 * Phone-auth triage + users/{uid} bootstrap after Terms acceptance.
 * Same contract as the portal (web-x9-my) and native AuthContext.
 */
import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { signOut, type User } from 'firebase/auth';
import {
  CURRENT_FREE_SNAPSHOT,
  PLAN_TERMS_VERSION,
} from '@x9/usage';

import { getFirebaseAuth, getFirebaseDb } from '../config/firebase';
import { COUNTRY_OPTIONS } from './countryCode';
import { buildWebClientStamp } from './clientOrigin';

export const TERMS_VERSION = '2026-07';

export type PhoneAuthTriage = 'returning' | 'new';

function languageFromDialOrPhone(phoneE164: string | null | undefined): 'pt' | 'en' {
  if (phoneE164?.startsWith('+55')) return 'pt';
  return 'en';
}

function phoneCountryFromE164(phoneE164: string | null | undefined): string {
  if (!phoneE164) return 'US';
  const match = [...COUNTRY_OPTIONS]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((c) => phoneE164.startsWith(c.dialCode));
  return match?.iso ?? 'US';
}

function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export async function resolvePhoneAuthTriage(uid: string): Promise<PhoneAuthTriage> {
  const snap = await getDoc(doc(getFirebaseDb(), 'users', uid));
  if (!snap.exists()) return 'new';
  return snap.data()?.termsAcceptedAt ? 'returning' : 'new';
}

export async function hasEstablishedProfile(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(getFirebaseDb(), 'users', uid));
  if (!snap.exists()) return false;
  const data = snap.data();
  return !!(data?.phoneNumber && data?.authProvider);
}

export async function discardUnconsentedProfile(uid: string): Promise<void> {
  const ref = doc(getFirebaseDb(), 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  if (data?.termsAcceptedAt) return;
  if (data?.phoneNumber && data?.authProvider) return;
  await deleteDoc(ref);
}

export async function touchReturningSignIn(uid: string): Promise<void> {
  const userDocRef = doc(getFirebaseDb(), 'users', uid);
  const snap = await getDoc(userDocRef);
  const existingClient = snap.exists() ? snap.data()?.client : null;
  await setDoc(
    userDocRef,
    {
      lastSignInTime: serverTimestamp(),
      updatedAt: serverTimestamp(),
      timezone: deviceTimezone(),
      client: {
        ...buildWebClientStamp('use_web', existingClient),
        seenAt: serverTimestamp(),
      },
    },
    { merge: true },
  );
}

export async function acceptTermsAndCreateProfile(user: User): Promise<void> {
  const phoneNumber = user.phoneNumber;
  if (!phoneNumber) {
    throw new Error('Verified phone missing from session');
  }

  const phoneCountry = phoneCountryFromE164(phoneNumber);
  const language = languageFromDialOrPhone(phoneNumber);
  const userDocRef = doc(getFirebaseDb(), 'users', user.uid);
  const existingSnap = await getDoc(userDocRef);
  const existingClient = existingSnap.exists() ? existingSnap.data()?.client : null;

  await setDoc(
    userDocRef,
    {
      uid: user.uid,
      phoneNumber,
      phoneE164: phoneNumber,
      phoneCountry,
      displayName: `User ${phoneNumber.slice(-4)}`,
      photoURL: user.photoURL ?? null,
      email: user.email ?? null,
      emailVerified: false,
      phoneVerified: true,
      timezone: deviceTimezone(),
      termsAcceptedAt: serverTimestamp(),
      termsVersion: TERMS_VERSION,
      planTermsVersion: PLAN_TERMS_VERSION,
      freeLimitsSnapshot: CURRENT_FREE_SNAPSHOT,
      planTermsStampedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      lastSignInTime: serverTimestamp(),
      updatedAt: serverTimestamp(),
      authProvider: 'phone',
      preferences: {
        notifications: {
          taskAssigned: true,
          statusChanged: true,
          deadlineApproaching: true,
          comments: true,
        },
        theme: 'light',
        language,
      },
      stats: {
        tasksCreated: 0,
        tasksCompleted: 0,
        totalPoints: 0,
      },
      client: {
        ...buildWebClientStamp('use_web', existingClient),
        seenAt: serverTimestamp(),
      },
    },
    { merge: true },
  );
}

export async function discardUnregisteredAuthUser(): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return;
  try {
    await user.delete();
  } catch {
    await signOut(auth);
  }
}

/** Decline Terms: keep established accounts, drop brand-new Auth + stubs. */
export async function declineTermsAndResetSession(): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return;
  const established = await hasEstablishedProfile(user.uid);
  await discardUnconsentedProfile(user.uid);
  if (established) {
    await signOut(auth);
  } else {
    await discardUnregisteredAuthUser();
  }
}

export async function continueIfReturning(uid: string): Promise<boolean> {
  const triage = await resolvePhoneAuthTriage(uid);
  if (triage !== 'returning') return false;
  try {
    await touchReturningSignIn(uid);
  } catch {
    // Non-fatal — session is still valid.
  }
  return true;
}
